import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface TimeoudData {
  TeamOneScore?: number;
  TeamTwoScore?: number;
  TimeoutCalledByUuid?: string;
  ServerWhenTimeoutCalledFullName?: string;
  NextRallyResult?: string;
  TimeoutStarted?: string;
  start?: string;
}

interface Player {
  name: string;
  uuid: string;
  teamUuid: string;
}

interface InfoModalProps {
  data: TimeoudData | null;
  playerList: Player[];
  isOpen: boolean;
  onClose: React.MouseEventHandler;
}

function InfoModal({ isOpen, onClose, data, playerList }: InfoModalProps) {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 767);

  const getTeamByUUID = () => {
    const player = playerList.find((p) => p.uuid === data?.TimeoutCalledByUuid);
    const playerTeam = playerList.filter((p) => p.teamUuid === player?.teamUuid);
    if (playerTeam.length === 1) {
      return playerTeam[0]?.name;
    } else if (playerTeam.length === 2) {
      return playerTeam[0]?.name + ' & ' + playerTeam[1]?.name;
    } else {
      return 'Unknown';
    }
  };

  const getSecondTeamByUUID = () => {
    const player = playerList.find((p) => p.uuid === data?.TimeoutCalledByUuid);
    const playerTeam = playerList.filter((p) => p.teamUuid !== player?.teamUuid);
    if (playerTeam.length === 1) {
      return playerTeam[0]?.name;
    } else if (playerTeam.length === 2) {
      return playerTeam[0]?.name + ' & ' + playerTeam[1]?.name;
    } else {
      return 'Unknown';
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }

    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 767);
    };

    window.addEventListener('resize', handleResize);

    // Set initial state
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.1 } },
  };

  const contentVariants = {
    hidden: { y: '-50%', opacity: 0 },
    visible: {
      y: isDesktop ? '20%' : '25%',
      opacity: 1,
      transition: { duration: 0.1 },
    },
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial='hidden'
          animate='visible'
          exit='hidden'
          variants={backdropVariants}
          className='fixed inset-0 z-50'
          onClick={onClose}
          aria-labelledby='modal-title'
          role='dialog'
          aria-modal='true'
        >
          <div className='flex h-[75vh] items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0'>
            <div
              className='bg-opacity/75 fixed inset-0 bg-gray-500 transition-opacity'
              aria-hidden='true'
            />
            <span className='hidden sm:inline-block sm:h-screen sm:align-top' aria-hidden='true'>
              &#8203;
            </span>
            <motion.div
              initial='hidden'
              animate='visible'
              exit='hidden'
              variants={contentVariants}
              className='inline-block w-full overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all sm:max-w-lg sm:align-middle'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='relative px-4 py-2 sm:px-6'>
                <div className='sm:flex sm:items-start'>
                  <h3
                    className='w-full text-lg font-medium leading-6 text-gray-900'
                    id='modal-title'
                  >
                    Timeout Details
                  </h3>
                </div>
                <div
                  className=' absolute right-3 top-2 cursor-pointer rounded-md bg-gray-300 px-2 py-1 text-xs font-medium uppercase text-white'
                  onClick={onClose}
                >
                  x
                </div>
              </div>
              <div
                className='grow overflow-y-auto bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4'
                style={{ minHeight: '70vh', maxHeight: '70vh' }}
              >
                <div className='mt-2 flex flex-col gap-4'>
                  <div className='flex flex-col'>
                    <span className='font-medium'>Timeout called by:</span>
                    <span>{getTeamByUUID()}</span>
                  </div>
                  <div className='flex flex-col'>
                    <span className='font-medium'>Server when timeout was called:</span>
                    <span>{data?.ServerWhenTimeoutCalledFullName}</span>
                  </div>
                  <div className='flex flex-col'>
                    <span className='font-medium'>Score when timeout was called:</span>
                    <span>
                      {data?.TeamOneScore}-{data?.TeamTwoScore}-
                      {data?.NextRallyResult === 'Win' ? 1 : 2}
                    </span>
                  </div>
                  <div className='flex flex-col'>
                    <span className='font-medium'>Next rally won by after timeout called:</span>
                    {data?.NextRallyResult === 'Win' ? (
                      <span>{getTeamByUUID()}</span>
                    ) : (
                      <span>{getSecondTeamByUUID()}</span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default InfoModal;
